import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import Accordion from "../components/Accordion";
import { useAuth } from "../context/AuthContext";
import { Plus, BookOpen, FlaskConical, Calendar, Trash2, Loader2, ChevronRight, ChevronLeft, Save, X, User, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
    { value: "non_evalue", label: "Non évalué", className: "bg-rule text-ink/60" },
    { value: "non_acquis", label: "Non acquis", className: "bg-red-100 text-red-800 border border-red-200" },
    { value: "partiel", label: "Partiellement", className: "bg-amber-100 text-amber-800 border border-amber-200" },
    { value: "acquis", label: "Acquis", className: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
];

export default function Courses() {
    const { user } = useAuth();
    const isFormateur = user?.role === "formateur";
    const isResponsable = user?.role === "responsable";
    const isAdmin = user?.role === "admin";
    const canViewCourses = isFormateur || isAdmin || isResponsable || user?.role === "alternant";
    // Responsable + admin can view any course in their establishment in read-only mode.
    const canFetchAlternants = isFormateur || isResponsable || isAdmin;

    const [courses, setCourses] = useState([]);
    const [alternants, setAlternants] = useState([]);
    const [referentiel, setReferentiel] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCourse, setEditingCourse] = useState(null); // course being edited
    const [viewingCourse, setViewingCourse] = useState(null); // course in read-only mode (responsable)
    const [filterPeriod, setFilterPeriod] = useState("all");  // dd/mois./yyyy filter

    const reload = async () => {
        try {
            const [cRes, aRes, rRes] = await Promise.all([
                api.get("/courses"),
                canFetchAlternants ? api.get("/establishment/alternants") : Promise.resolve({ data: [] }),
                api.get("/referentiels"),
            ]);
            setCourses(cRes.data);
            setAlternants(aRes.data);
            setReferentiel(rRes.data?.electrotechnique?.competencies || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const removeCourse = async (id, ev) => {
        ev?.stopPropagation();
        const course = courses.find((c) => c.id === id);
        const isOwn = course && course.author_id === user?.id;
        const msg = isOwn
            ? "Supprimer cette séance ? Cette action est irréversible."
            : `Supprimer la synthèse de ${course?.author_name || "ce formateur"} ? Toutes les évaluations associées seront perdues. Cette action est irréversible.`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/courses/${id}`);
            toast.success("Séance supprimée");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const canEditCourse = (course) => isAdmin || (isFormateur && course.author_id === user?.id);
    const canDeleteCourse = (course) => canEditCourse(course) || isResponsable;
    const canViewCourseDetail = (course) => canEditCourse(course) || isResponsable;

    const onCardClick = (course) => {
        if (canEditCourse(course)) {
            setEditingCourse(course);
            setShowForm(true);
        } else if (canViewCourseDetail(course)) {
            setViewingCourse(course);
        }
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingCourse(null);
    };

    // Editable courses = those the current user can actually edit (for prev/next)
    const editableCourses = useMemo(() => {
        if (user?.role === "admin") return courses;
        if (isFormateur) return courses.filter((c) => c.author_id === user?.id);
        return [];
    }, [courses, user, isFormateur]);

    const gotoSibling = (offset) => {
        if (!editingCourse) return;
        const idx = editableCourses.findIndex((c) => c.id === editingCourse.id);
        if (idx === -1) return;
        const next = editableCourses[idx + offset];
        if (next) setEditingCourse(next);
    };

    // Group courses by author (formateur) — each group keeps original sort by date desc.
    // Apply period filter first so groups disappear when their courses are filtered out.
    const FR_MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
    const formatPeriod = (iso) => {
        if (!iso || iso.length < 10) return "—";
        const [y, m, d] = iso.split("-");
        const mm = parseInt(m, 10);
        if (!mm || mm < 1 || mm > 12) return iso;
        return `${parseInt(d, 10).toString().padStart(2, "0")}/${FR_MONTHS[mm - 1]}/${y}`;
    };

    // Build sorted list of unique periods (date dd/mois./yyyy) for the filter dropdown
    const periodOptions = useMemo(() => {
        const seen = new Map();
        courses.forEach((c) => {
            if (!c.date) return;
            const iso = c.date.slice(0, 10);
            if (!seen.has(iso)) seen.set(iso, formatPeriod(iso));
        });
        return Array.from(seen.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([value, label]) => ({ value, label }));
    }, [courses]);

    const filteredCourses = useMemo(() => {
        if (filterPeriod === "all") return courses;
        return courses.filter((c) => (c.date || "").slice(0, 10) === filterPeriod);
    }, [courses, filterPeriod]);

    // Group courses by author (formateur) — each group keeps original sort by date desc
    const grouped = useMemo(() => {
        const byAuthor = new Map();
        filteredCourses.forEach((c) => {
            const key = c.author_id || "unknown";
            if (!byAuthor.has(key)) {
                byAuthor.set(key, {
                    author_id: key,
                    author_name: c.author_name || c.author_email || "Formateur",
                    courses: [],
                });
            }
            byAuthor.get(key).courses.push(c);
        });
        // Stable ordering: groups with most recent course first
        const groups = Array.from(byAuthor.values());
        groups.sort((a, b) => {
            const da = a.courses[0]?.date || "";
            const db = b.courses[0]?.date || "";
            return db.localeCompare(da);
        });
        return groups;
    }, [filteredCourses]);

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="courses-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
                    <div>
                        <div className="label-eyebrow mb-2">Évaluations pédagogiques</div>
                        <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">
                            Suivi de <span className="text-orange">progression</span>
                        </h1>
                        <p className="mt-3 text-sm text-muted2 max-w-xl">
                            Tracez chaque séance, taggez les alternants présents et évaluez chaque compétence ciblée — Qualiopi compatible.
                        </p>
                    </div>
                    {isFormateur && (
                        <button onClick={() => { setEditingCourse(null); setShowForm(true); }} className="btn-primary !py-3 self-start sm:self-auto" data-testid="courses-new-button">
                            <Plus size={16} /> Nouvelle évaluation pédagogique
                        </button>
                    )}
                </div>

                {showForm && (isFormateur || isAdmin) && (
                    <CourseForm
                        editing={editingCourse}
                        alternants={alternants}
                        competencies={referentiel}
                        onClose={closeForm}
                        onSaved={() => { closeForm(); reload(); }}
                        onGotoPrev={editingCourse ? () => gotoSibling(-1) : null}
                        onGotoNext={editingCourse ? () => gotoSibling(1) : null}
                        hasPrev={editingCourse && editableCourses.findIndex((c) => c.id === editingCourse.id) > 0}
                        hasNext={editingCourse && editableCourses.findIndex((c) => c.id === editingCourse.id) < editableCourses.length - 1}
                        siblingIndex={editingCourse ? editableCourses.findIndex((c) => c.id === editingCourse.id) : -1}
                        siblingTotal={editableCourses.length}
                    />
                )}

                {/* PERIOD FILTER (shown once there's at least one course) */}
                {courses.length > 0 && (
                    <div className="panel p-3 bg-surface flex flex-wrap items-center gap-3 mb-4" data-testid="courses-filters">
                        <div className="flex items-center gap-1.5 text-xs font-mono text-muted2">
                            <Filter size={13} /> Filtrer par période :
                        </div>
                        <select
                            className="field-input !py-1 !text-xs"
                            value={filterPeriod}
                            onChange={(e) => setFilterPeriod(e.target.value)}
                            data-testid="courses-period-filter"
                        >
                            <option value="all">Toutes les dates</option>
                            {periodOptions.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        {filterPeriod !== "all" && (
                            <button
                                onClick={() => setFilterPeriod("all")}
                                className="text-[11px] text-orange hover:underline"
                                data-testid="courses-filter-reset"
                            >
                                Réinitialiser
                            </button>
                        )}
                        <span className="text-[11px] font-mono text-muted2 ml-auto">
                            {filteredCourses.length} séance{filteredCourses.length > 1 ? "s" : ""}
                        </span>
                    </div>
                )}

                {viewingCourse && (
                    <CourseDetailModal
                        course={viewingCourse}
                        alternants={alternants}
                        competencies={referentiel}
                        onClose={() => setViewingCourse(null)}
                    />
                )}

                {loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm">Chargement…</div>
                ) : courses.length === 0 ? (
                    <div className="panel p-12 text-center" data-testid="courses-empty-state">
                        <BookOpen size={36} strokeWidth={1.5} className="text-muted2 mx-auto" />
                        <h3 className="h-display text-xl font-semibold mt-4">Aucune séance enregistrée</h3>
                        <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
                            {isFormateur ? "Cliquez sur « Nouveau cours / TP » pour ajouter votre première séance." : "Votre formateur n'a encore enregistré aucune séance vous concernant."}
                        </p>
                    </div>
                ) : filteredCourses.length === 0 ? (
                    <div className="panel p-10 text-center text-sm text-muted2" data-testid="courses-no-period-match">
                        Aucune séance pour cette date. <button onClick={() => setFilterPeriod("all")} className="text-orange underline">Réinitialiser le filtre</button>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="courses-grouped">
                        {grouped.map((group) => (
                            <Accordion
                                key={group.author_id}
                                icon={User}
                                title={`Évaluations pédagogiques de ${group.author_name}`}
                                subtitle={`${group.courses.length} séance(s) enregistrée(s)`}
                                count={group.courses.length}
                                testId={`courses-group-${group.author_id}`}
                            >
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {group.courses.map((c) => {
                                        const isClickable = canViewCourseDetail(c);
                                        const editable = canEditCourse(c);
                                        const deletable = canDeleteCourse(c);
                                        return (
                                        <div
                                            key={c.id}
                                            onClick={() => isClickable && onCardClick(c)}
                                            className={`panel p-5 group relative ${isClickable ? "cursor-pointer hover:border-orange transition-colors" : ""}`}
                                            data-testid={`course-card-${c.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="pill bg-ink text-white inline-flex items-center">
                                                    {c.kind === "tp" ? <><FlaskConical size={11} className="mr-1" /> TP</> : <><BookOpen size={11} className="mr-1" /> Cours</>}
                                                </span>
                                                {deletable && (
                                                    <button
                                                        onClick={(ev) => removeCourse(c.id, ev)}
                                                        className="text-muted2 hover:text-destructive p-1"
                                                        title={editable ? "Supprimer" : "Supprimer cette synthèse de formateur"}
                                                        data-testid={`course-delete-${c.id}`}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <h3 className="h-display text-lg font-semibold mt-3">{c.title}</h3>
                                            <div className="mt-2 text-xs font-mono text-muted2 flex items-center gap-1.5"><Calendar size={12} /> {formatPeriod(c.date)} {c.duration_hours ? `· ${c.duration_hours}h` : ""}</div>
                                            {c.description && <p className="text-xs text-ink/70 mt-3 line-clamp-3">{c.description}</p>}
                                            <div className="mt-4 pt-3 border-t border-rule text-xs text-muted2 font-mono flex items-center justify-between">
                                                <span>{(c.evaluations || []).length} alternant(s) évalué(s)</span>
                                                {isClickable && (
                                                    <span className="text-orange opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {editable ? "Modifier →" : "Voir le détail →"}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                    })}
                                </div>
                            </Accordion>
                        ))}
                    </div>
                )}

                {!isFormateur && (
                    <div className="mt-10">
                        <Link to={`/progression/${user?.id}`} className="btn-outline" data-testid="my-progression-link">
                            Voir ma progression complète <ChevronRight size={14} />
                        </Link>
                    </div>
                )}
            </main>
        </div>
    );
}

function CourseForm({ editing, alternants, competencies, onClose, onSaved, onGotoPrev, onGotoNext, hasPrev, hasNext, siblingIndex, siblingTotal }) {
    const [title, setTitle] = useState(editing?.title || "");
    const [description, setDescription] = useState(editing?.description || "");
    const [date, setDate] = useState(editing?.date || new Date().toISOString().slice(0, 10));
    const [kind, setKind] = useState(editing?.kind || "cours");
    const [duration, setDuration] = useState(editing?.duration_hours ? String(editing.duration_hours) : "");
    const [selectedAlt, setSelectedAlt] = useState(() => editing?.evaluations?.map((ev) => ev.alternant_id).filter(Boolean) || []);
    const [altNiveauFilter, setAltNiveauFilter] = useState("all"); // "all" | "1ere" | "2eme" | "other"
    // evaluations[alt_id] = { code: status }
    const [evals, setEvals] = useState(() => {
        if (!editing?.evaluations) return {};
        const map = {};
        editing.evaluations.forEach((ev) => {
            if (!ev.alternant_id) return;
            map[ev.alternant_id] = {};
            (ev.competencies || []).forEach((c) => { map[ev.alternant_id][c.code] = c.status; });
        });
        return map;
    });
    // Which competencies the formateur wants to evaluate in this session.
    // Auto-seed from existing evaluations (for editing), else empty so the formateur picks.
    const [selectedComps, setSelectedComps] = useState(() => {
        if (!editing?.evaluations) return [];
        const codes = new Set();
        editing.evaluations.forEach((ev) => (ev.competencies || []).forEach((c) => codes.add(c.code)));
        return Array.from(codes);
    });
    // Free-text comment per alternant for this session
    const [comments, setComments] = useState(() => {
        if (!editing?.evaluations) return {};
        const m = {};
        editing.evaluations.forEach((ev) => {
            if (ev.alternant_id && ev.comment) m[ev.alternant_id] = ev.comment;
        });
        return m;
    });
    const [activeAlt, setActiveAlt] = useState(() => editing?.evaluations?.[0]?.alternant_id || null);
    const [saving, setSaving] = useState(false);

    const isEditing = !!editing?.id;
    const editingId = editing?.id || null;

    // Reset form state when switching to another course via prev/next arrows
    useEffect(() => {
        setTitle(editing?.title || "");
        setDescription(editing?.description || "");
        setDate(editing?.date || new Date().toISOString().slice(0, 10));
        setKind(editing?.kind || "cours");
        setDuration(editing?.duration_hours ? String(editing.duration_hours) : "");
        setSelectedAlt(editing?.evaluations?.map((ev) => ev.alternant_id).filter(Boolean) || []);
        const map = {};
        (editing?.evaluations || []).forEach((ev) => {
            if (!ev.alternant_id) return;
            map[ev.alternant_id] = {};
            (ev.competencies || []).forEach((c) => { map[ev.alternant_id][c.code] = c.status; });
        });
        setEvals(map);
        const codes = new Set();
        (editing?.evaluations || []).forEach((ev) => (ev.competencies || []).forEach((c) => codes.add(c.code)));
        setSelectedComps(Array.from(codes));
        const cm = {};
        (editing?.evaluations || []).forEach((ev) => {
            if (ev.alternant_id && ev.comment) cm[ev.alternant_id] = ev.comment;
        });
        setComments(cm);
        setActiveAlt(editing?.evaluations?.[0]?.alternant_id || null);
    }, [editingId]);

    const toggleAlt = (id) => {
        setSelectedAlt((s) => {
            if (s.includes(id)) {
                const next = s.filter((x) => x !== id);
                if (activeAlt === id) setActiveAlt(next[0] || null);
                return next;
            }
            const next = [...s, id];
            if (!activeAlt) setActiveAlt(id);
            return next;
        });
    };

    const toggleComp = (code) => {
        setSelectedComps((s) => s.includes(code) ? s.filter((x) => x !== code) : [...s, code]);
    };

    // Filter alternants by niveau tab
    const visibleAlternants = useMemo(() => {
        if (altNiveauFilter === "all") return alternants;
        if (altNiveauFilter === "other") return alternants.filter((a) => !a.niveau);
        return alternants.filter((a) => a.niveau === altNiveauFilter);
    }, [alternants, altNiveauFilter]);

    // Quick-select all visible alternants of the active niveau filter
    const selectAllVisible = () => {
        const visibleIds = visibleAlternants.map((a) => a.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedAlt.includes(id));
        if (allSelected) {
            setSelectedAlt((s) => s.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelectedAlt((s) => Array.from(new Set([...s, ...visibleIds])));
        }
    };

    const setStatus = (altId, code, status) => {
        setEvals((s) => ({
            ...s,
            [altId]: { ...(s[altId] || {}), [code]: status },
        }));
    };

    const setComment = (altId, text) => {
        setComments((s) => ({ ...s, [altId]: text }));
    };

    const submit = async ({ keepOpen = false } = {}) => {
        if (!title.trim() || !date) {
            toast.error("Titre et date requis");
            return false;
        }
        if (selectedAlt.length === 0) {
            toast.error("Sélectionnez au moins un alternant");
            return false;
        }
        setSaving(true);
        try {
            const evaluations = selectedAlt.map((altId) => {
                const altEvals = evals[altId] || {};
                // Only keep the competencies the formateur actively selected for this session
                const comps = selectedComps
                    .map((code) => ({ code, status: altEvals[code] || "non_evalue" }))
                    .filter((c) => c.status && c.status !== "non_evalue");
                const obj = {
                    alternant_id: altId,
                    competencies: comps,
                };
                const cm = (comments[altId] || "").trim();
                if (cm) obj.comment = cm;
                return obj;
            });
            const payload = {
                title,
                description,
                date,
                kind,
                duration_hours: duration ? parseFloat(duration) : null,
                evaluations,
            };
            if (isEditing) {
                await api.put(`/courses/${editing.id}`, payload);
                toast.success("Séance mise à jour");
            } else {
                await api.post("/courses", payload);
                toast.success("Séance enregistrée");
            }
            if (!keepOpen) onSaved();
            return true;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const navigate = async (direction) => {
        // Auto-save current edits before moving (so nothing is lost)
        if (isEditing) {
            const ok = await submit({ keepOpen: true });
            if (!ok) return;
        }
        if (direction === "prev" && onGotoPrev) onGotoPrev();
        else if (direction === "next" && onGotoNext) onGotoNext();
    };

    return (
        <div className="panel p-6 mb-8 border-2 border-orange/30" data-testid="course-form">
            <div className="flex items-center justify-between mb-4 gap-3">
                <div className="min-w-0">
                    <h2 className="h-display text-xl font-semibold truncate">{isEditing ? "Modifier la séance" : "Nouvelle séance"}</h2>
                    {isEditing && (
                        <div className="text-[11px] font-mono text-muted2 mt-0.5">
                            Modifications illimitées · auto-tracé
                            {siblingTotal > 1 && siblingIndex >= 0 && (
                                <span> · Séance {siblingIndex + 1}/{siblingTotal}</span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {isEditing && siblingTotal > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate("prev")}
                                disabled={!hasPrev || saving}
                                title="Enregistrer et aller à la séance précédente"
                                className="p-2 border border-rule hover:border-orange hover:text-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                data-testid="course-prev-button"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate("next")}
                                disabled={!hasNext || saving}
                                title="Enregistrer et aller à la séance suivante"
                                className="p-2 border border-rule hover:border-orange hover:text-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                data-testid="course-next-button"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </>
                    )}
                    <button onClick={onClose} className="text-muted2 hover:text-ink p-2" data-testid="course-close-button"><X size={16} /></button>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <label className="label-eyebrow mb-2 block">Intitulé</label>
                    <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: TP Câblage armoire électrique TGBT" data-testid="course-title-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Type</label>
                    <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="course-kind-select">
                        <option value="cours">Cours magistral</option>
                        <option value="tp">Travaux pratiques (TP)</option>
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Date</label>
                    <input type="date" className="field-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="course-date-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Durée (h)</label>
                    <input type="number" min="0" step="0.5" className="field-input" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="2" data-testid="course-duration-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-eyebrow mb-2 block">Description (optionnel)</label>
                    <textarea rows={2} className="field-input resize-none" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Objectifs et contenus de la séance" data-testid="course-description-input" />
                </div>
            </div>

            <div className="mt-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="label-eyebrow">Alternants présents</div>
                    {/* Niveau filter tabs */}
                    <div className="flex flex-wrap gap-1" data-testid="course-alt-niveau-filter">
                        {[
                            { key: "all", label: "Tous" },
                            { key: "1ere", label: "1ère année BTS" },
                            { key: "2eme", label: "2ème année BTS" },
                        ].map((t) => {
                            const active = altNiveauFilter === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setAltNiveauFilter(t.key)}
                                    className={`px-2.5 py-1 text-[11px] transition-colors ${active ? "bg-ink text-white border border-ink" : "bg-paper border border-rule hover:border-ink/40"}`}
                                    data-testid={`course-alt-niveau-${t.key}`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={selectAllVisible}
                            className="px-2.5 py-1 text-[11px] bg-orange/10 text-orange border border-orange/30 hover:bg-orange/20"
                            data-testid="course-select-all-visible"
                            title="Tout sélectionner / désélectionner dans la vue actuelle"
                        >
                            {visibleAlternants.every((a) => selectedAlt.includes(a.id)) && visibleAlternants.length > 0 ? "Tout désélectionner" : "Tout sélectionner"}
                        </button>
                    </div>
                </div>
                {alternants.length === 0 ? (
                    <p className="text-xs text-muted2 italic">Aucun alternant inscrit. Partagez votre code d&apos;établissement.</p>
                ) : visibleAlternants.length === 0 ? (
                    <p className="text-xs text-muted2 italic">Aucun alternant avec ce niveau. Changez de filtre.</p>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {visibleAlternants.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => toggleAlt(a.id)}
                                className={`text-left p-3 border rounded-sm text-sm transition-all ${selectedAlt.includes(a.id) ? "border-orange bg-orange/5" : "border-rule hover:border-ink/40"}`}
                                data-testid={`course-alt-${a.id}`}
                            >
                                <div className="font-medium flex items-center gap-1.5">
                                    {a.prenom} {a.nom}
                                    {a.niveau && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{a.niveau === "1ere" ? "1ère" : "2ème"}</span>}
                                </div>
                                <div className="text-[11px] text-muted2 font-mono truncate">{a.email}</div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Competency selector — formateur picks which competencies matter for this session */}
            {selectedAlt.length > 0 && (
                <div className="mt-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div>
                            <div className="label-eyebrow">Compétences à évaluer</div>
                            <div className="text-[11px] text-muted2 mt-0.5">Sélectionne uniquement les compétences pertinentes pour cette séance ({selectedComps.length}/{competencies.length})</div>
                        </div>
                        <div className="flex gap-1">
                            <button type="button" onClick={() => setSelectedComps(competencies.map((c) => c.code))} className="px-2.5 py-1 text-[11px] bg-paper border border-rule hover:border-ink/40" data-testid="course-comps-select-all">Toutes</button>
                            <button type="button" onClick={() => setSelectedComps([])} className="px-2.5 py-1 text-[11px] bg-paper border border-rule hover:border-ink/40" data-testid="course-comps-clear">Aucune</button>
                        </div>
                    </div>
                    <div className="border border-rule p-3 max-h-48 overflow-y-auto" data-testid="course-comps-selector">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
                            {competencies.map((c) => {
                                const picked = selectedComps.includes(c.code);
                                return (
                                    <label key={c.code} className={`flex items-start gap-2 text-xs cursor-pointer p-1 -m-1 rounded-sm ${picked ? "bg-orange/5" : "hover:bg-surface"}`}>
                                        <input
                                            type="checkbox"
                                            checked={picked}
                                            onChange={() => toggleComp(c.code)}
                                            className="mt-0.5 accent-orange shrink-0"
                                            data-testid={`course-comps-${c.code}`}
                                        />
                                        <span className="font-mono text-orange font-bold w-10 shrink-0">{c.code}</span>
                                        <span className="flex-1 leading-snug">{c.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {selectedAlt.length > 0 && selectedComps.length > 0 && (
                <div className="mt-6">
                    <div className="label-eyebrow mb-3">Évaluation par alternant</div>
                    {/* Tabs to swap between alternants without scrolling */}
                    <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-rule" data-testid="course-eval-tabs">
                        {selectedAlt.map((altId) => {
                            const a = alternants.find((x) => x.id === altId);
                            const isActive = activeAlt === altId;
                            const filled = selectedComps.filter((code) => {
                                const st = evals[altId]?.[code];
                                return st && st !== "non_evalue";
                            }).length;
                            return (
                                <button
                                    key={altId}
                                    type="button"
                                    onClick={() => setActiveAlt(altId)}
                                    className={`px-3 py-1.5 text-xs transition-all ${isActive ? "bg-ink text-white" : "bg-paper border border-rule hover:border-ink/40"}`}
                                    data-testid={`course-eval-tab-${altId}`}
                                >
                                    <span className="font-semibold">{a?.prenom} {a?.nom}</span>
                                    {filled > 0 && (
                                        <span className={`ml-2 text-[10px] font-mono ${isActive ? "text-orange" : "text-muted2"}`}>· {filled}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {activeAlt && (() => {
                        const altId = activeAlt;
                        const a = alternants.find((x) => x.id === altId);
                        return (
                            <div className="border border-rule rounded-sm p-4" data-testid={`course-eval-panel-${altId}`}>
                                <div className="font-semibold text-sm mb-3 flex items-center gap-2">
                                    {a?.prenom} {a?.nom}
                                    {a?.niveau && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{a.niveau === "1ere" ? "1ère" : "2ème"}</span>}
                                </div>
                                <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                                    {selectedComps.map((code) => {
                                        const c = competencies.find((x) => x.code === code);
                                        if (!c) return null;
                                        const cur = evals[altId]?.[c.code] || "non_evalue";
                                        return (
                                            <div key={c.code} className="flex items-center gap-2 text-xs">
                                                <span className="font-mono text-orange font-bold w-10 shrink-0">{c.code}</span>
                                                <span className="flex-1 truncate">{c.label}</span>
                                                <select
                                                    value={cur}
                                                    onChange={(e) => setStatus(altId, c.code, e.target.value)}
                                                    className="field-input !py-1 !px-2 !text-xs w-36 shrink-0"
                                                    data-testid={`course-eval-${altId}-${c.code}`}
                                                >
                                                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Comment per alternant for this session */}
                                <div className="mt-4 pt-3 border-t border-rule">
                                    <label className="label-eyebrow mb-1.5 block">Commentaire sur la progression</label>
                                    <textarea
                                        rows={3}
                                        value={comments[altId] || ""}
                                        onChange={(e) => setComment(altId, e.target.value)}
                                        placeholder={`Exemple : ${a?.prenom || "L'alternant"} progresse bien sur la partie câblage mais doit encore consolider la partie automatisme…`}
                                        maxLength={2000}
                                        className="field-input resize-none !text-xs"
                                        data-testid={`course-comment-${altId}`}
                                    />
                                    <div className="text-[10px] text-muted2 font-mono mt-1">{(comments[altId] || "").length}/2000 · visible par l&apos;alternant et ses autres formateurs dans /progression</div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
                <button onClick={onClose} className="btn-outline">Annuler</button>
                <button onClick={() => submit()} disabled={saving} className="btn-primary" data-testid="course-save-button">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {saving ? "Enregistrement…" : (isEditing ? "Mettre à jour la séance" : "Enregistrer la séance")}
                </button>
            </div>
        </div>
    );
}

function CourseDetailModal({ course, alternants, competencies, onClose }) {
    const altById = useMemo(() => {
        const m = {};
        (alternants || []).forEach((a) => { m[a.id] = a; });
        return m;
    }, [alternants]);

    const competencyById = useMemo(() => {
        const m = {};
        (competencies || []).forEach((c) => { m[c.code] = c; });
        return m;
    }, [competencies]);

    const evaluations = course.evaluations || [];
    const allCompCodes = useMemo(() => {
        const s = new Set();
        evaluations.forEach((ev) => (ev.competencies || []).forEach((c) => s.add(c.code)));
        return Array.from(s);
    }, [evaluations]);

    const statusMeta = (status) => STATUSES.find((s) => s.value === status) || STATUSES[0];

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="course-detail-modal" onClick={onClose}>
            <div className="bg-paper max-w-3xl w-full max-h-[90vh] flex flex-col border border-rule" onClick={(ev) => ev.stopPropagation()}>
                <div className="p-5 border-b border-rule flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="pill bg-ink text-white inline-flex items-center">
                                {course.kind === "tp" ? <><FlaskConical size={11} className="mr-1" /> TP</> : <><BookOpen size={11} className="mr-1" /> Cours</>}
                            </span>
                            <span className="pill bg-orange/10 text-orange border border-orange/30 !text-[10px]">Lecture seule</span>
                        </div>
                        <h2 className="h-display text-xl font-semibold mt-2">{course.title}</h2>
                        <div className="text-xs font-mono text-muted2 mt-1.5 flex flex-wrap gap-3">
                            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {course.date}</span>
                            {course.duration_hours && <span>· {course.duration_hours}h</span>}
                            <span>· Par {course.author_name || "Formateur"}</span>
                            <span>· {evaluations.length} alternant(s)</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted2 hover:text-ink p-2" data-testid="course-detail-close" aria-label="Fermer"><X size={16} /></button>
                </div>

                <div className="flex-1 overflow-y-auto scroll-visible p-5 space-y-5">
                    {course.description && (
                        <div>
                            <div className="label-eyebrow mb-2">Description</div>
                            <p className="text-sm text-ink/85 whitespace-pre-wrap leading-relaxed">{course.description}</p>
                        </div>
                    )}

                    {allCompCodes.length > 0 && (
                        <div>
                            <div className="label-eyebrow mb-2">Compétences évaluées dans cette séance</div>
                            <div className="flex flex-wrap gap-1.5">
                                {allCompCodes.map((code) => {
                                    const c = competencyById[code];
                                    return (
                                        <span key={code} className="pill bg-paper border border-rule !text-[11px]" title={c?.label || ""}>
                                            <span className="font-mono">{code}</span>
                                            {c?.label && <span className="text-muted2 ml-1">— {c.label.length > 40 ? `${c.label.slice(0, 40)}…` : c.label}</span>}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div>
                        <div className="label-eyebrow mb-2">Évaluations par alternant</div>
                        {evaluations.length === 0 ? (
                            <p className="text-xs text-muted2 font-mono">Aucun alternant évalué dans cette séance.</p>
                        ) : (
                            <div className="space-y-3">
                                {evaluations.map((ev) => {
                                    const alt = altById[ev.alternant_id];
                                    const altName = alt ? `${alt.prenom || ""} ${alt.nom || ""}`.trim() : "Alternant";
                                    const acquis = (ev.competencies || []).filter((c) => c.status === "acquis").length;
                                    const partiel = (ev.competencies || []).filter((c) => c.status === "partiel").length;
                                    const non = (ev.competencies || []).filter((c) => c.status === "non_acquis").length;
                                    return (
                                        <div key={ev.alternant_id || altName} className="border border-rule p-3" data-testid={`course-detail-eval-${ev.alternant_id}`}>
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <span className="font-semibold text-sm">{altName || "Alternant"}</span>
                                                {alt?.email && <span className="text-[11px] font-mono text-muted2">{alt.email}</span>}
                                                <span className="text-[11px] font-mono text-emerald-700">✓ {acquis} acquis</span>
                                                <span className="text-[11px] font-mono text-amber-700">◑ {partiel} partiel</span>
                                                <span className="text-[11px] font-mono text-red-700">✗ {non} non acquis</span>
                                            </div>
                                            {(ev.competencies || []).length > 0 && (
                                                <div className="grid sm:grid-cols-2 gap-1.5 mt-2.5">
                                                    {ev.competencies.map((c) => {
                                                        const meta = statusMeta(c.status);
                                                        const ref = competencyById[c.code];
                                                        return (
                                                            <div key={c.code} className={`text-[11px] px-2 py-1.5 ${meta.className}`} title={ref?.label || ""}>
                                                                <span className="font-mono font-semibold">{c.code}</span>
                                                                <span className="ml-1.5">— {meta.label}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {ev.comment && (
                                                <div className="mt-2 pt-2 border-t border-rule">
                                                    <div className="label-eyebrow text-[10px] mb-1">Commentaire du formateur</div>
                                                    <p className="text-xs text-ink/80 whitespace-pre-wrap leading-relaxed">{ev.comment}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 border-t border-rule flex justify-end">
                    <button onClick={onClose} className="btn-outline !text-xs" data-testid="course-detail-done">Fermer</button>
                </div>
            </div>
        </div>
    );
}


export const COMPETENCY_STATUSES = STATUSES;
